import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Card, message, Spin, Modal, Upload } from 'antd';
import { SaveOutlined, PlusOutlined, ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import { getCourse, updateCourse, createCourse } from './api';
import config from './config';

const { TextArea } = Input;
const { Option } = Select;

interface CourseFormProps {
  courseId?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface CourseData {
  title: string;
  description?: string;
  shortDescription?: string;
  image_url?: string;
  status: 'draft' | 'published';
}

const CourseForm: React.FC<CourseFormProps> = ({ courseId, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | undefined>(undefined);
  const isEditing = !!courseId;

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  const loadCourse = async () => {
    if (!courseId) return;
    setInitialLoading(true);
    try {
      const course = await getCourse(courseId);
      form.setFieldsValue({
        title: course.title,
        description: course.description || '',
        shortDescription: course.shortDescription || '',
        image_url: course.image_url || '',
        status: course.status || 'draft',
      });
      setImagePreview(course.image_url || undefined);
      setHasChanges(false);
    } catch (error) {
      message.error('Ошибка загрузки курса');
      console.error(error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values: CourseData) => {
    setLoading(true);
    try {
      if (isEditing && courseId) {
        await updateCourse(courseId, values);
        message.success('Курс успешно обновлен');
      } else {
        await createCourse(values);
        message.success('Курс успешно создан');
      }
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      message.error(error.message || 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Подтверждение',
        content: 'У вас есть несохраненные изменения. Вы уверены, что хотите выйти?',
        okText: 'Да, выйти',
        cancelText: 'Отмена',
        onOk: () => {
          setHasChanges(false);
          onCancel();
        },
      });
    } else {
      onCancel();
    }
  };

  const handleValuesChange = () => {
    setHasChanges(true);
  };

  // --- Загрузка изображения ---
  const handleImageUpload = async (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }
    if (info.file.status === 'done') {
      setUploading(false);
      const url = info.file.response?.url;
      if (url) {
        form.setFieldsValue({ image_url: url });
        setImagePreview(url);
        setHasChanges(true);
        message.success('Изображение успешно загружено');
      }
    } else if (info.file.status === 'error') {
      setUploading(false);
      message.error('Ошибка загрузки изображения');
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            style={{ padding: 0, height: 'auto' }}
          />
          {isEditing ? 'Редактировать курс' : 'Создать новый курс'}
        </div>
      }
      style={{ maxWidth: 800, margin: '0 auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        initialValues={{
          status: 'draft',
        }}
      >
        <Form.Item
          name="title"
          label="Название курса"
          rules={[{ required: true, message: 'Пожалуйста, введите название курса' }]}
        >
          <Input placeholder="Введите название курса" />
        </Form.Item>

        <Form.Item
          name="shortDescription"
          label="Короткое описание (до 255 символов)"
          rules={[{ max: 255, message: 'Максимум 255 символов' }]}
        >
          <TextArea rows={2} placeholder="Короткое описание курса" showCount maxLength={255} />
        </Form.Item>

        <Form.Item
          name="description"
          label="Описание"
        >
          <TextArea 
            rows={4} 
            placeholder="Введите описание курса"
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item label="Обложка курса">
          <Upload
            name="file"
            action={`${config.apiUrl}/courses/upload`}
            headers={{ Authorization: `Bearer ${localStorage.getItem('jwt') || ''}` }}
            showUploadList={false}
            onChange={handleImageUpload}
            accept="image/*"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              Загрузить изображение
            </Button>
          </Upload>
          {imagePreview && (
            <div style={{ marginTop: 12 }}>
              <img src={imagePreview} alt="Обложка" style={{ maxWidth: 200, borderRadius: 8, boxShadow: '0 2px 8px #eee' }} />
            </div>
          )}
        </Form.Item>

        <Form.Item
          name="image_url"
          label="URL изображения"
        >
          <Input placeholder="https://example.com/image.png" />
        </Form.Item>

        <Form.Item
          name="status"
          label="Статус"
          rules={[{ required: true, message: 'Пожалуйста, выберите статус' }]}
        >
          <Select placeholder="Выберите статус">
            <Option value="draft">Черновик</Option>
            <Option value="published">Опубликован</Option>
          </Select>
        </Form.Item>

        <Form.Item>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>
              Отмена
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={isEditing ? <SaveOutlined /> : <PlusOutlined />}
            >
              {isEditing ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default CourseForm; 